import React, { useState, useRef, useEffect } from 'react';
import useBundleTranslation from 'i18n';
import { ColorResult, SketchPicker, CompactPicker } from 'react-color';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { SxProps } from '@mui/system';
import { Box, Button, Popper, Stack, TextField } from '@mui/material';
import { RawFormElementProps } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import styles from 'components/common/color-picker/ColorPicker.styles';
import IconMi from 'components/common/icon/IconMi';
import { IconType } from 'components/common/icon/IconHandler';
import IconHandler from 'components/common/icon/IconHandler';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { FormComponentBuilder } from 'components/common/form/layout';

export interface ColorPickerIconType extends IconType {
    fill: 'text' | 'background';
}

export interface FormControlColorPickerProps extends FormControlProps {
    view?: 'field' | 'button' | 'icon' | 'icon-button';
    pickerType?: 'default' | 'onlyColors';
    canReset?: boolean;
    defaultValue?: string;
    buttonText?: string;
    sx?: SxProps;
    presetColors?: string[];
    simpleMode?: boolean;
    onColorChange?: (color: string) => void;
    icon?: ColorPickerIconType;
    toggleShow?: boolean;
    disableAlpha?: boolean;
}

export default function ColorPicker({
    controlProps: props,
    elementProps: elementProps,
}: FormElementControlPropsType<FormControlColorPickerProps>) {
    const {
        view = 'field',
        name,
        canReset = false,
        defaultValue = '',
        buttonText,
        presetColors,
        pickerType = 'default',
        simpleMode = false,
        icon,
        toggleShow,
        disableAlpha = true,
    } = props;
    const [colorVal, setColorVal] = useState<string>('');
    const [showPicker, setShowPicker] = useState<boolean>(false);

    const FIELD_COLOR_NAME = `${name}_user_tmp`;
    const colorPickerWrapperRef = useRef<HTMLInputElement>();
    const { t } = useBundleTranslation(['components/common/color_picker']);

    const formatColorValue = (value: string) => {
        if (disableAlpha && value) {
            return value.toUpperCase();
        } else {
            return value;
        }
    };

    useEffect(() => {
        if (simpleMode && props.value) {
            setColorVal(formatColorValue(props.value));
            return;
        }

        const initialValue = formatColorValue(props.form.hookFormGetValues(name) ?? '');
        setColorVal(initialValue);
        props.form.hookFormSetValue(name, initialValue, { shouldDirty: false });
    }, []);

    useEffect(() => {
        setColorVal(formatColorValue(props.value));
    }, [props.value]);

    const handleColorChange = (colorObj: ColorResult) => {
        let color = colorObj.hex.toUpperCase();
        if (!disableAlpha && colorObj.rgb.a != 1) {
            color = `rgba(${colorObj.rgb.r}, ${colorObj.rgb.g}, ${colorObj.rgb.b}, ${colorObj.rgb.a})`;
        }
        setColorVal(color);
        if (simpleMode && props.onColorChange) {
            props.onColorChange(color);
            return;
        }

        props.form.hookFormSetValue(name, color, { shouldDirty: true });
    };

    const elementPropsHidden = {
        data: {},
        form: props.form,
    } as RawFormElementProps;

    const onReset = (event: any) => {
        event.preventDefault();
        handleColorChange({ hex: defaultValue } as ColorResult);
    };

    const showPickerPlugin = () => {
        if (!toggleShow || (toggleShow && !showPicker)) {
            setShowPicker(true);
            setTimeout(() => {
                const hexInput = document.querySelector('.colorPickerPlugin input') as HTMLElement | null;
                if (hexInput) hexInput.focus();
            }, 100);
        } else {
            setShowPicker(false);
        }
    };

    const colorFieldProps = {
        name: FIELD_COLOR_NAME,
        value: colorVal,
        fullWidth: true,
        onClick: showPickerPlugin,
        className: elementProps?.designVariant ? 'design-variant--' + elementProps.designVariant : undefined,
        InputProps: {
            className: showPicker ? 'Mui-focused' : undefined,
            readOnly: true,
            startAdornment: <Box sx={{ ...styles.previewColor, backgroundColor: colorVal }} />,
            endAdornment: !elementProps?.designVariant ? (
                <Stack sx={styles.pickerIcon}>
                    <IconMi icon="color-picker" fontSize="16" />
                </Stack>
            ) : undefined,
        },
    };

    //ExtJs ColorPalette default colors palette
    // prettier-ignore
    const onlyColorsDefaultPreset = [
        '#000000', '#993300', '#333300', '#003300', '#003366', '#000080', '#333399', '#333333',
        '#800000', '#FF6600', '#808000', '#008000', '#008080', '#0000FF', '#666699', '#808080',
        '#FF0000', '#FF9900', '#99CC00', '#339966', '#33CCCC', '#3366FF', '#800080', '#969696',
        '#FF00FF', '#FFCC00', '#FFFF00', '#00FF00', '#00FFFF', '#00CCFF', '#993366', '#C0C0C0',
        '#FF99CC', '#FFCC99', '#FFFF99', '#CCFFCC', '#CCFFFF', '#99CCFF', '#CC99FF', '#FFFFFF'
    ];

    // Style for icon view
    const iconStyle: React.CSSProperties = { display: 'inline-flex' };
    iconStyle[icon?.fill == 'background' ? 'backgroundColor' : 'color'] = colorVal ?? undefined;

    return (
        <ClickAwayListener
            onClickAway={() => {
                setShowPicker(false);
            }}
        >
            <Box
                ref={colorPickerWrapperRef}
                sx={{ display: view !== 'field' ? 'inline-flex' : undefined }}
                className={'colorPicker'}
            >
                <ReactHookFormController
                    elementProps={prepareFormElementProps({
                        ...elementPropsHidden,
                        component: {
                            uid: name,
                            name: name,
                            component: 'FormHidden',
                        },
                    })}
                />
                {view === 'icon' && (
                    <Box
                        component={'span'}
                        sx={iconStyle}
                        onClick={showPickerPlugin}
                        className={'colorPickerIconHolder'}
                    >
                        <Box component="span" className={'colorPickerIcon'}>
                            <IconHandler icon={icon ?? { type: 'mi', value: 'color-picker' }} />
                        </Box>
                        {buttonText && <Box className={'colorPickerIconHolderLabel'}>{t(buttonText)}</Box>}
                    </Box>
                )}
                {view === 'icon-button' && (
                    <Button
                        variant="outlined"
                        className={'colorPickerIconHolder min-width--icon'}
                        color="neutral"
                        onClick={showPickerPlugin}
                        sx={{ '&.colorPickerIconHolder': iconStyle }}
                    >
                        <IconHandler
                            icon={icon ?? { type: 'mi', value: 'color-picker' }}
                            sx={{ fontSize: '16px' }}
                            className={'colorPickerIcon'}
                        />
                    </Button>
                )}
                {view === 'field' && <TextField {...colorFieldProps} sx={styles.field} />}
                {view === 'button' && (
                    <Button
                        variant="outlined"
                        color="neutral"
                        style={{ borderColor: colorVal ?? undefined }}
                        onClick={showPickerPlugin}
                    >
                        {t(buttonText ?? 'button_text')}
                    </Button>
                )}

                <Popper
                    sx={{ py: 0.5, zIndex: (theme) => theme.zIndex.modal + 1 }}
                    open={showPicker}
                    anchorEl={colorPickerWrapperRef.current}
                    placement="bottom-start"
                    nonce={undefined}
                >
                    <Box sx={styles.colorPickerWrapper}>
                        {pickerType === 'default' && (
                            <SketchPicker
                                className="colorPickerPlugin"
                                disableAlpha={disableAlpha}
                                color={colorVal}
                                onChange={handleColorChange}
                                presetColors={presetColors}
                            />
                        )}
                        {pickerType === 'onlyColors' && (
                            <CompactPicker
                                className="colorPickerPlugin--compact"
                                color={colorVal}
                                onChange={handleColorChange}
                                colors={presetColors ?? onlyColorsDefaultPreset}
                                styles={{
                                    default: {
                                        compact: {
                                            width: '160px',
                                        },
                                    },
                                }}
                            />
                        )}

                        {canReset && (
                            <Box sx={styles.colorPickerResetWrap}>
                                <Box onClick={onReset} component="a" sx={styles.colorPickerReset}>
                                    <IconMi sx={{ fontSize: '16px', mr: 1 }} icon="refresh-cw" /> {t('button_reset')}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}

export class ColorPickerBuilder extends FormComponentBuilder {
    prepareProps(): FormControlColorPickerProps {
        return {
            ...this.controlProps,
            view: this.elementProps.componentProps?.view ?? 'field',
            defaultValue: this.elementProps.componentProps?.defaultValue ?? '',
            canReset: this.elementProps.componentProps?.canReset ?? false,
            buttonText: this.elementProps.componentProps?.buttonText ?? undefined,
            presetColors: this.elementProps.componentProps?.presetColors ?? undefined,
            pickerType: this.elementProps.componentProps?.pickerType ?? 'default',
            icon: this.elementProps.componentProps?.icon ?? undefined,
            toggleShow: this.elementProps.componentProps?.toggleShow ?? undefined,
            disableAlpha: this.elementProps.componentProps?.disableAlpha,
        };
    }
}
